import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, Chip, CircularProgress
} from '@mui/material';
import { getSalesMetrics } from '../../api/queriesAPI';

const SalesPerformanceTable = () => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await getSalesMetrics();
        if (response && response.success) {
          setMetrics(response.data);
        } else {
          setError("Failed to fetch metrics.");
        }
      } catch (err) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)', p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Executive Performance Metrics
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(90deg, #f8f9fa, #fff)' }}>
                <TableCell sx={{ fontWeight: 700 }}>Executive</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Assigned Leads</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Uncontacted</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Follow-up Compliance</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Converted</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Lost</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Reassigned</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Revenue (₹)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">No data available.</TableCell>
                </TableRow>
              ) : (
                metrics.map((row, index) => (
                  <TableRow key={row.user_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: '#667eea' }}>
                          {row.fullName ? row.fullName[0].toUpperCase() : 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.fullName}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.role}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={row.assigned} size="small" variant="outlined" color="primary" />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color={row.uncontacted > 0 ? "error" : "text.secondary"}>
                        {row.uncontacted}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={`${row.followUpCompliance}%`} 
                        size="small" 
                        color={row.followUpCompliance < 50 ? "error" : (row.followUpCompliance >= 80 ? "success" : "warning")} 
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="success.main" fontWeight="600">{row.converted}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" color="error.main">{row.lost}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{row.reassigned}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="700">
                        ₹ {row.revenue.toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default SalesPerformanceTable;
